<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import type { PlayerProfile, PlayerPosition, Team } from '@/types/models';
import AppModal from '@/components/ui/AppModal.vue';
import { NButton } from 'naive-ui';
import { Printer, ShieldCheck } from 'lucide-vue-next';

interface BattingSlot {
  slot: number;
  player: PlayerProfile | null;
  pos: PlayerPosition;
}

interface Props {
  modelValue: boolean;
  team: Team;
  battingOrder: BattingSlot[];
  startingPitcher: PlayerProfile | null;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:modelValue', val: boolean): void;
}>();

const { t } = useI18n();

const handlePrint = () => {
  window.print();
};
</script>

<template>
  <AppModal
    :model-value="modelValue"
    @update:model-value="(val) => emit('update:modelValue', val)"
    :title="t('lineupCard.modalTitle')"
    :subtitle="team.name"
    max-width="lg"
  >
    <div class="space-y-6 print:p-0">
      <!-- Lineup Card Printable Sheet Container -->
      <div class="bg-slate-950 border border-slate-800 p-6 rounded-2xl space-y-4 print:border-black print:bg-white print:text-black">
        <!-- Official Header -->
        <div class="flex items-center justify-between border-b border-slate-800 pb-3">
          <div>
            <span class="text-[10px] uppercase tracking-widest text-sky-400 font-extrabold block">{{ t('lineupCard.headerBadge') }}</span>
            <h2 class="text-lg font-black text-slate-100 uppercase tracking-tight">{{ team.name }}</h2>
            <p class="text-xs text-slate-400">
              {{ t('lineupCard.division') }}: {{ team.division }} &bull; {{ t('lineupCard.headCoach') }}: {{ team.headCoach }}
            </p>
          </div>
          <div class="text-right">
            <span class="text-xs font-mono font-bold text-slate-300">{{ t('lineupCard.date') }}: {{ new Date().toLocaleDateString() }}</span>
            <div class="flex items-center gap-1 text-[11px] text-emerald-400 font-semibold justify-end mt-1">
              <ShieldCheck class="w-3.5 h-3.5" /> {{ t('lineupCard.verified') }}
            </div>
          </div>
        </div>

        <!-- 9 Starters Table -->
        <table class="w-full text-xs text-left border-collapse">
          <thead>
            <tr class="border-b border-slate-800 text-[10px] uppercase tracking-wider text-slate-400">
              <th class="py-2 px-2 text-center w-8">#</th>
              <th class="py-2 px-2">{{ t('roster.playerName') }}</th>
              <th class="py-2 px-2 text-center w-12">{{ t('lineupCard.num') }}</th>
              <th class="py-2 px-2 text-center w-12">{{ t('lineupCard.pos') }}</th>
              <th class="py-2 px-2 text-center w-12">{{ t('roster.batsThrows') }}</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-800/80">
            <tr v-for="item in battingOrder" :key="item.slot" class="hover:bg-slate-900/50">
              <td class="py-2 px-2 text-center font-bold text-sky-400 font-mono">{{ item.slot }}</td>
              <td class="py-2 px-2 font-bold text-slate-200">
                {{ item.player ? `${item.player.lastName} ${item.player.firstName}` : '—' }}
              </td>
              <td class="py-2 px-2 text-center font-mono font-bold text-slate-300">
                {{ item.player ? `#${item.player.jerseyNumber}` : '—' }}
              </td>
              <td class="py-2 px-2 text-center font-mono font-extrabold text-amber-400">
                {{ item.pos }}
              </td>
              <td class="py-2 px-2 text-center font-mono text-slate-400">
                {{ item.player ? `${item.player.bats}/${item.player.throws}` : '—' }}
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Signatures Footer -->
        <div class="pt-4 border-t border-slate-800 grid grid-cols-2 gap-6 text-[10px] text-slate-400">
          <div>
            <div class="border-b border-slate-700 h-6 mb-1"></div>
            <span>{{ t('lineupCard.managerSig') }}</span>
          </div>
          <div>
            <div class="border-b border-slate-700 h-6 mb-1"></div>
            <span>{{ t('lineupCard.umpireSig') }}</span>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex items-center justify-between pt-2">
        <NButton quaternary @click="emit('update:modelValue', false)">
          {{ t('common.close') }}
        </NButton>
        <div class="flex items-center gap-2">
          <NButton type="primary" @click="handlePrint" class="font-bold text-xs shadow-lg shadow-sky-500/20">
            <template #icon><Printer class="w-4 h-4" /></template>
            {{ t('lineupCard.printBtn') }}
          </NButton>
        </div>
      </div>
    </div>
  </AppModal>
</template>
